import { useState } from 'react'

import {
  INTAKE_FORM_ERROR_CODES,
  type IntakeFormRefusal,
} from '@/definitions/intakeForm'
import { useCreateIntakeForm } from '@/hooks/useCreateIntakeForm'
import { isTimeoutError } from '@/utils/apiErrors'

import { describeSaveFailure } from '../utils/describeSaveFailure'
import { parseFormTemplate } from '../utils/parseFormTemplate'
import { readRefusal } from '../utils/readRefusal'

// Calling a timeout a failure invites the retry that comes back refusing a name
// the first attempt already took.
const SAVE_UNCONFIRMED_MESSAGE =
  'The server did not answer in time, so whether the form was saved is not known. ' +
  'Close this and check the list before trying again.'

interface UploadCallbacks {
  /** A name the organization already uses — reported on the field that fixes it. */
  onNameRefused: (message: string) => void
  onSaved: (displayName: string) => void
}

interface SubmitArgs {
  displayName: string
  formTemplateJson: string
}

/**
 * The save flow behind the upload modal: what to send, and what each refusal means.
 *
 * Nothing here decides what is acceptable. Only text that is not JSON at all is
 * judged locally; every other verdict comes from the API's refusal.
 */
export const useUploadIntakeForm = (orgId: string, callbacks: UploadCallbacks) => {
  const createForm = useCreateIntakeForm()

  const [refusal, setRefusal] = useState<IntakeFormRefusal | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  // A refusal describes the text it was raised against, so an edit retires it and
  // the next press submits afresh rather than acknowledging warnings never shown.
  const retireRefusal = () => {
    setRefusal(null)
    setProblem(null)
  }

  const warnings = refusal?.warnings ?? []
  // An empty list makes this an acknowledgement of nothing, on a write nothing here
  // can undo — so a refusal whose warnings did not survive reading is not offered
  // as one, and falls through to its message instead.
  const isAwaitingConfirmation =
    refusal?.code === INTAKE_FORM_ERROR_CODES.UNACKNOWLEDGED_WARNINGS &&
    warnings.length > 0

  const submit = async ({ displayName, formTemplateJson }: SubmitArgs) => {
    const parsed = parseFormTemplate(formTemplateJson)
    if (!parsed.ok) {
      setRefusal(null)
      setProblem(parsed.message)
      return
    }

    // Stored verbatim, and nothing here can rename a form — so a pasted trailing
    // space would be permanent, invisible on a list that shows no identifier, and
    // would make the correctly typed name a duplicate afterwards.
    const trimmed = displayName.trim()

    try {
      await createForm.mutateAsync({
        orgId,
        data: {
          display_name: trimmed,
          form_template: parsed.value,
          acknowledge_warnings: isAwaitingConfirmation,
        },
      })
      callbacks.onSaved(trimmed)
    } catch (error) {
      if (isTimeoutError(error)) {
        setRefusal(null)
        setProblem(SAVE_UNCONFIRMED_MESSAGE)
        return
      }
      const named = readRefusal(error)
      if (named?.code === INTAKE_FORM_ERROR_CODES.DUPLICATE_FORM_NAME) {
        callbacks.onNameRefused(named.message)
        return
      }
      setRefusal(named)
      setProblem(named === null ? describeSaveFailure(error) : null)
    }
  }

  return {
    refusal,
    problem,
    warnings,
    isAwaitingConfirmation,
    isSaving: createForm.isPending,
    retireRefusal,
    submit,
  }
}
